import React, { Fragment, type MouseEvent, type ReactNode, useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
// reason: FIXME at moment we rely too much on custom styling, we need all accessibilites from menu item but own styling
// eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
import { Collapse, Menu, MenuItem } from '@mui/material'
import { useTheme } from '@dhl-ui/theme'
import { List, Box, Divider, useIsMobileVariant, Typography, BadgeInline, StackRow } from '@dhl-ui/components'
import { usePage } from '../../context'
import { type PageMenuItem } from './types'
import { hasSubMenu } from './helpers/has-submenu'
import { getActiveTopLevel } from './helpers/get-active-top-level'
import { MainMenuItem } from './MainMenuItem'
import { Toggle } from './Toggle'
import { SubMenuItem } from './SubMenuItem'

type Props = {
  /**
   * Navigational items
   */
  items: PageMenuItem[]

  /**
   * When you need something below the main menu
   */
  endNode?: ReactNode

  /**
   * Tell consumer which item the user has selected so they can navigate or do their own logic
   */
  onSelect: (arg: PageMenuItem) => void
}

type CollapsedMenuState = {
  anchorEl: HTMLElement
  itemKey: string
} | null

export const MainMenu = ({ items, endNode, onSelect }: Props) => {
  const { spacing } = useTheme()
  const { isExpanded, setIsOpen } = usePage()
  const isMobile = useIsMobileVariant()

  const [openMenu, setOpenMenu] = useState<string | null>(() => {
    const matched = getActiveTopLevel(items)
    return matched ? matched?.key : null
  })

  const isMenuOpen = (item: PageMenuItem) => openMenu === item.key

  const [collapsedMenu, setCollapsedMenu] = useState<CollapsedMenuState>(null)
  const open = Boolean(collapsedMenu)

  /**
   * When our submenu is open when collapsed and then we expand we do not want to have it open even when its not showing
   * because after collapsing again the position is gone
   */
  useEffect(() => {
    if (open && isExpanded) {
      setCollapsedMenu(null)
    }
  }, [isExpanded, open])

  const handleClick = (event: React.MouseEvent<HTMLElement>, itemKey: string) => {
    event.preventDefault()
    setCollapsedMenu({ anchorEl: event.currentTarget, itemKey })
  }

  const handleClose = () => {
    setCollapsedMenu(null)
  }

  const handleItemClick = (item: PageMenuItem) => (e: MouseEvent<HTMLElement>) => {
    // we render href inside our links, but just so user can open it in new tab
    e.preventDefault()
    e.stopPropagation()

    // user can click on an already active item again, we make sure correct item is open
    if (item.isActive) {
      // reason: done explicitly
      // eslint-disable-next-line n/no-sync
      flushSync(() => setOpenMenu(null))
      // reason: done explicitly
      // eslint-disable-next-line n/no-sync
      flushSync(() => setOpenMenu(getActiveTopLevel(items)?.key || null))
    }

    // make sure we close the menu when we are on mobile...
    setIsOpen(false)
    handleClose()

    onSelect(item)
  }

  const handleMainMenuItemClick = (item: PageMenuItem) => (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    if (isExpanded) {
      if (isMenuOpen(item)) {
        setOpenMenu(null)
      } else {
        setOpenMenu(item.key)
      }
    } else {
      handleClick(event, item.key)
    }
  }

  return (
    <>
      {items.length ? (
        <List
          component="div"
          data-testid="page-side-menu-list"
          sx={{ mx: isExpanded ? 1 : 0.5, mt: 1, backgroundColor: 'pure.light' }}
        >
          {items.map((item, i) => {
            if (hasSubMenu(item)) {
              const hasActiveChild = item.items?.some((subItem) => subItem.isActive)
              const id = item.key

              const markAsActive = item.isActive || ((!isExpanded || !isMenuOpen(item)) && hasActiveChild)

              return (
                <Fragment key={item.key}>
                  <MainMenuItem
                    id={id}
                    icon={item.icon}
                    label={item.label}
                    link={item.link}
                    onClick={handleMainMenuItemClick(item)}
                    isActive={markAsActive}
                    isDisabled={item.isDisabled}
                    isOpen={isMenuOpen(item)}
                    isExpanded={isExpanded}
                    isExpandable
                    isCollapsedMenuOpenForItem={collapsedMenu?.itemKey === item.key}
                  />

                  {isExpanded ? (
                    <Collapse in={isMenuOpen(item)} unmountOnExit sx={{ mb: 1 }}>
                      <Box sx={{ pl: isExpanded ? 2 : 0, pb: 1 }}>
                        {item.items?.map((subItem) => (
                          <SubMenuItem
                            id={subItem.key}
                            aria-labelledby={id}
                            key={subItem.key}
                            label={subItem.label}
                            link={subItem.link}
                            badge={subItem.badge}
                            onClick={handleItemClick(subItem)}
                            isActive={subItem.isActive}
                            isDisabled={subItem.isDisabled}
                          />
                        ))}
                      </Box>
                    </Collapse>
                  ) : (
                    <Menu
                      id="submenu-menu"
                      key={`submenu-menu-${id}`}
                      aria-labelledby={id}
                      anchorEl={collapsedMenu?.anchorEl ?? null}
                      open={open && collapsedMenu?.itemKey === item.key}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      slotProps={{
                        backdrop: {
                          sx: { background: 'transparent' },
                        },
                        paper: {
                          sx: {
                            minWidth: spacing(24),
                          },
                        },
                      }}
                      elevation={2}
                      sx={{ ml: 8 }}
                    >
                      {item.items?.map((subItem) => (
                        <MenuItem
                          data-testid={`page-side-menu-${subItem.key}`}
                          key={subItem.key}
                          onClick={handleItemClick(subItem)}
                          selected={subItem.isActive}
                          sx={{
                            color: subItem.isActive ? 'primary.main' : 'grey.700',
                            fontWeight: subItem.isActive ? 'bold' : undefined,
                            bgcolor: subItem.isActive ? 'primary.states.hover' : undefined,
                            '&.Mui-selected': {
                              opacity: 1,
                              // ensure correct background color for the selected state:
                              bgcolor: subItem.isActive ? 'primary.states.hover' : undefined,
                              '&:hover': {
                                bgcolor: subItem.isActive ? 'components.navbar.selectedHover' : 'action.hover',
                              },
                            },
                            '&.Mui-selected.Mui-focusVisible, &.Mui-focusVisible': {
                              backgroundColor: subItem.isActive ? 'components.navbar.selectedHover' : 'action.hover',
                            },
                          }}
                        >
                          <StackRow>
                            <Box>{subItem.label}</Box>
                            {subItem.badge && (
                              <BadgeInline
                                variant="standard"
                                badgeContent={subItem.badge.count}
                                color={subItem.badge.color}
                              />
                            )}
                          </StackRow>
                        </MenuItem>
                      ))}
                    </Menu>
                  )}
                </Fragment>
              )
            }

            return (
              <Fragment key={item.key}>
                {item.sectionTitle &&
                  // reason: we have two states but still readable
                  // eslint-disable-next-line no-nested-ternary
                  (isExpanded ? (
                    <Typography variant="h6" sx={{ mt: i === 0 ? 0 : 1, p: spacing(1.75, 2.25, 1, 2.25) }}>
                      {isExpanded ? item.sectionTitle : ''}
                    </Typography>
                  ) : i !== 0 ? (
                    <Divider />
                  ) : null)}

                <MainMenuItem
                  id={item.key}
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  badge={item.badge}
                  link={item.link}
                  onClick={handleItemClick(item)}
                  isActive={item.isActive}
                  isDisabled={item.isDisabled}
                  isExpanded={isExpanded}
                />
              </Fragment>
            )
          })}
        </List>
      ) : null}
      <Box sx={{ mt: 'auto' }}>{endNode}</Box>
      {!isMobile ? <Toggle /> : null}
    </>
  )
}
